import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { Box, Flex } from "theme-ui";
import PropTypes from "prop-types";
import { Icon } from "../atoms";
import { colors, typography } from "../../styles/theme";
import AskNavosButton from "./AskNavosButton";
import { initBeaconBar, openBeaconBarSearch } from "./beaconBar";

const NavosHeroActions = ({
  searchInputId = "navos-help-search",
  customStyles,
  variant = "default",
  compact = false,
}) => {
  useEffect(() => {
    initBeaconBar();
  }, []);

  const handleSearchTrigger = () => {
    openBeaconBarSearch();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key !== "/" && event.code !== "Slash") return;

    event.preventDefault();
    event.stopPropagation();
    openBeaconBarSearch();
  };

  const isHeaderVariant = variant === "header";

  return (
    <Flex
      sx={{
        ...getOuterContainerStyles(isHeaderVariant, compact),
        ...customStyles,
      }}
    >
      <SearchBar
        as="button"
        type="button"
        data-navos-search-trigger
        onClick={handleSearchTrigger}
        onKeyDown={handleSearchKeyDown}
        aria-label="Search help articles"
        compact={compact}
        isHeader={isHeaderVariant}
      >
        <Icon name="Search" size={compact ? 20 : 24} />
        {!compact && (
          <>
            <SearchInput
              id={searchInputId}
              type="search"
              placeholder="Search"
              tabIndex={-1}
              readOnly
            />
            <ShortcutBadge aria-hidden="true">/</ShortcutBadge>
          </>
        )}
      </SearchBar>

      <AskNavosButton compact={compact} />
    </Flex>
  );
};

NavosHeroActions.propTypes = {
  searchInputId: PropTypes.string,
  customStyles: PropTypes.object,
  variant: PropTypes.oneOf(["default", "header"]),
  compact: PropTypes.bool,
};

export default NavosHeroActions;

const getOuterContainerStyles = (isHeaderVariant, compact) => ({
  flexDirection: "row",
  alignItems: "stretch",
  justifyContent: "center",
  gap: isHeaderVariant ? ["0.75rem", "0.75rem", "1.5rem", "1.5rem"] : ["1rem", "1rem", "1rem", "1.5rem"],
  width: isHeaderVariant && compact ? "auto" : "100%",
  maxWidth:
    isHeaderVariant && !compact ? "33.438rem" : isHeaderVariant ? "none" : "37.063rem",
  minWidth: 0,
  mx: isHeaderVariant ? undefined : "auto",
  alignSelf: isHeaderVariant ? undefined : "center",
  mt: isHeaderVariant ? 0 : ["2rem", "2rem", "3rem", "3rem"],
  flexShrink: isHeaderVariant ? 0 : undefined,
  boxSizing: "border-box",
});

const SearchBar = styled(Flex)`
  align-items: center;
  gap: 0.75rem;
  flex: ${({ compact, isHeader }) =>
    compact ? "0 0 auto" : "1 1 0"};
  width: ${({ compact }) => (compact ? "4.2rem" : "auto")};
  max-width: ${({ compact, isHeader }) =>
    compact ? "4.2rem" : isHeader ? "none" : "28rem"};
  min-width: ${({ compact }) => (compact ? "4.2rem" : 0)};
  min-height: ${({ compact, isHeader }) =>
    compact ? "2.75rem" : isHeader ? "3rem" : "4rem"};
  padding: ${({ compact }) => (compact ? "0" : "0 1rem 0 1.25rem")};
  justify-content: ${({ compact }) => (compact ? "center" : "flex-start")};
  overflow: hidden;
  background: ${colors.white};
  border: 1px solid ${colors.neutral200};
  border-radius: 9999px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: ${colors.neutral300};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  &:focus-within {
    border-color: ${colors.primaryMain};
    box-shadow: 0 0 0 3px rgba(170, 0, 234, 0.12);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  pointer-events: none;
  font-family: ${typography.textMd400.fontFamily};
  font-size: ${typography.textMd400.fontSize};
  font-weight: ${typography.textMd400.fontWeight};
  line-height: ${typography.textMd400.lineHeight};
  color: ${colors.neutral900};

  &::placeholder {
    color: ${colors.neutral400};
  }
`;

const ShortcutBadge = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.75rem;
  padding: 0 0.375rem;
  border-radius: 0.3rem;
  background: ${colors.neutral200};
  border: 1px solid ${colors.neutral200};
  pointer-events: none;
  font-family: ${typography.textSm.fontFamily};
  font-size: ${typography.textSm.fontSize};
  font-weight: ${typography.textSm.fontWeight};
  color: ${colors.black};
  line-height: 1;
`;
